import { useApolloClient, useMutation, DataProxy, gql } from '@apollo/client';
import { useCallback, useMemo, useContext, useEffect } from 'react';
import { debounce } from 'debounce';
import { FavoriteFragment } from '../../generated/types/FavoriteFragment';
import { tc } from '../../lib/trackingContext';
import { FavoritesContext } from './FavoritesProvider';
import {
  ADD_FAVORITE_MUTATION,
  GET_FAVORITES_QUERY,
  REMOVE_FAVORITE_MUTATION,
  GetFavoritesQuery
} from './favorites.rest';

export type UseFavoritesProps = {
  variant?: FavoriteFragment;
};

export const defaults = {
  data: {
    __typename: 'RestUserFavoritesData',
    attributes: {
      __typename: 'RestUserFavoritesAttributes',
      variantIds: []
    }
  }
};

export const FAVORITE_FRAGMENT = gql`
  fragment FavoriteFragment on Variant {
    id
  }
`;

const DEBOUNCE_AMOUNT_MS = 400;

const updateFavoritesCache = (cache: DataProxy, variantIdNumber: number, remove: boolean) => {
  const data = cache.readQuery({ query: GET_FAVORITES_QUERY });

  /* istanbul ignore if */
  if (!data) {
    return;
  }

  const variantIds = data.userFavorites.data.attributes.variantIds;
  const newVariantIds = remove
    ? variantIds.filter((id: number) => id !== variantIdNumber)
    : [variantIdNumber, ...variantIds];

  const resultData: GetFavoritesQuery = {
    userFavorites: {
      __typename: 'RestUserFavorites',
      data: {
        __typename: 'RestUserFavoritesData',
        attributes: {
          __typename: 'RestUserFavoritesAttributes',
          variantIds: newVariantIds
        }
      }
    }
  };

  cache.writeQuery({
    query: GET_FAVORITES_QUERY,
    data: resultData
  });
};

export const useFavorites = ({ variant }: UseFavoritesProps = {}) => {
  const variantId = variant?.id;
  // TODO: SearchApi and UserApi disagree on types here, clean this up after ECOM-733 is finished
  const variantIdNumber = Number(variantId);

  const client = useApolloClient();
  const { user, favorites, getFavorites, loaded } = useContext(FavoritesContext);

  useEffect(() => {
    getFavorites();
  }, [getFavorites]);

  const [addFavorite] = useMutation(ADD_FAVORITE_MUTATION, {
    update(cache) {
      updateFavoritesCache(cache, variantIdNumber, false);
    }
  });

  const [removeFavorite] = useMutation(REMOVE_FAVORITE_MUTATION, {
    update(cache) {
      updateFavoritesCache(cache, variantIdNumber, true);
    }
  });

  const debouncedAddFavorite = useMemo(
    () => debounce(addFavorite, DEBOUNCE_AMOUNT_MS, true),
    [addFavorite]
  );

  const debouncedRemoveFavorite = useMemo(
    () => debounce(removeFavorite, DEBOUNCE_AMOUNT_MS, true),
    [removeFavorite]
  );

  const isFavorited = useMemo(
    () => (variantIdNumber ? favorites.includes(variantIdNumber) : false),
    [favorites, variantIdNumber]
  );

  const handleFavorite = useCallback(async () => {
    if (!variantId) {
      // eslint-disable-next-line no-console
      console.warn('Unable to add/remove favorite: `variantId` is missing');
      return Promise.resolve();
    }

    // make sure favorites are fetched before running the mutation
    await client.query({ query: GET_FAVORITES_QUERY });

    if (isFavorited) {
      await debouncedRemoveFavorite({ variables: { id: variantId } });
      tc.track('Product Removed from Wishlist', {
        addToScopeOnce: { variant },
        addToTrackingEvent: {
          label: 'Favorites',
          wishlist_name: 'Favorites'
        }
      });
    } else {
      await debouncedAddFavorite({ variables: { id: variantId } });
      tc.track('Product Added to Wishlist', {
        addToScopeOnce: { variant },
        addToTrackingEvent: {
          label: 'Favorites',
          wishlist_name: 'Favorites'
        }
      });
    }
  }, [variant, variantId, client, isFavorited, debouncedRemoveFavorite, debouncedAddFavorite]);

  return { user, favorites, handleFavorite, isFavorited, loaded };
};
